/**
 * Attendance status options for live training attestation
 */
export const ATTENDANCE_OPTIONS = [
  { value: 'on_time', label: 'On-time & attended' },
  { value: 'late_or_left_early', label: 'Attended but late / left early' },
  { value: 'did_not_attend', label: 'Did not attend' }
];

/**
 * Configuration for each live training step
 * Maps step codes to their display properties and responsibilities
 */

export const LIVE_TRAINING_CONFIG = {
  onboarding__kickoff_live_training__kickoff_live_training: {
    title: 'Kickoff live training',
    description:
      'Review the requirements and set due dates based on your confirmed training schedule. To request a change to a confirmed training date, contact your dedicated onboarding team.',
    meetingKey: 'Kickoff live training',
    responsibilities: [
      'Confirm setup',
      'Create test patient (used during training by all staff members)',
      'Staff permissions',
      'Staff assignments',
      'Data ready / requested (if applicable)'
    ],
    attestationConfirmations: [
      'Backend and administrative setup in Myriad Motion has been completed, and required credentials have been added and validated.',
      'Kickoff and basic system training has been completed with the client.'
    ],
    confirmationKeys: ['backendSetupCompleted', 'kickoffTrainingCompleted']
  },
  onboarding__front_office_live_training__front_office_live_training: {
    title: 'Front office live training',
    description:
      'Review the requirements and set due dates based on your confirmed training schedule. To request a change to a confirmed training date, contact your dedicated onboarding team.',
    meetingKey: 'Front office live training',
    responsibilities: [
      'Healthcare program education',
      'Healthcare program setup (GFE + Pre-authorized healthcare)',
      'Test card reader + virtual terminal',
      'Upload schedule of fees',
      'Schedule test appointment',
      'Set up self-scheduling (if applicable)'
    ],
    attestationConfirmations: [
      'Front office workflows in Myriad Health have been reviewed, and front office training has been completed with the client.',
      'The client has been introduced to patient scheduling, check-in, and payment processing functionality.'
    ],
    confirmationKeys: ['frontOfficeWorkflowsReviewed', 'frontOfficeTrainingCompleted']
  },
  onboarding__clinical_live_training__clinical_live_training: {
    title: 'Clinical live training',
    description:
      'Review the requirements and set due dates based on your confirmed training schedule. To request a change to a confirmed training date, contact your dedicated onboarding team.',
    meetingKey: 'Clinical live training',
    responsibilities: [
      'Forms & templates built (verified by principal user)',
      'Codes / products added (if 3D charting enabled; enables checkout flow)',
      'Watch product tour / video'
    ],
    attestationConfirmations: [
      'Clinical workflows in Myriad Health have been reviewed, and clinical training has been completed with the client.',
      'The client has been introduced to available charting methods and clinical form functionality.'
    ],
    confirmationKeys: ['clinicalWorkflowsReviewed', 'chartingMethodsIntroduced']
  },
  onboarding__back_office_live_training__back_office_live_training: {
    title: 'Back office billing live training',
    description:
      'Review the requirements and set due dates based on your confirmed training schedule. To request a change to a confirmed training date, contact your dedicated onboarding team.',
    meetingKey: 'Back office live training',
    responsibilities: [
      'EDI enrollments submitted',
      'Verify schedule of fees & allowables',
      'Full data import verified (principal user)',
      'File a test claim / send invoice',
      'Run a patient payment (Card-on-file) (Pre-authorized healthcare form)',
      'Watch Product Tour / Video'
    ],
    attestationConfirmations: [
      'Back office billing workflows in Myriad Health have been reviewed, and billing training has been completed with the client.',
      'The client has been introduced to claims processing, ERA posting, and payment reconciliation functionality.'
    ],
    confirmationKeys: ['billingWorkflowsReviewed', 'backOfficeTrainingCompleted']
  },
  onboarding__hard_launch_live_training__hard_launch_live_training: {
    title: 'Hard launch live training',
    description:
      'Review the requirements and set due dates based on your confirmed training schedule. To request a change to a confirmed training date, contact your dedicated onboarding team.',
    meetingKey: 'Hard launch live training',
    responsibilities: [
      'Live appointments scheduled and/or live patient check-out',
      'Live patient check in and check out completed',
      'Full system use'
    ],
    attestationConfirmations: [
      'All onboarding phases have been completed, and the practice is fully prepared for live operations.',
      'The client has demonstrated proficiency in all trained workflows and is ready for hard launch.'
    ],
    confirmationKeys: ['firmGoLiveOccurred', 'hardLaunchSessionCompleted']
  }
};

/**
 * Get the configuration for a specific training step
 * @param {string} substepCode - The substep code
 * @returns {Object|null} The training configuration or null if not found
 */
export const getTrainingConfig = (substepCode) => {
  return LIVE_TRAINING_CONFIG[substepCode] || null;
};
